import type {QueryClient} from "@tanstack/react-query";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {invalidateOttopilotDataViewerQueries} from "~/components/ottopilotDataViewer/invalidateOttopilotDataViewerQueries";
import {JOURNEY_EVENTS_MODULE_DATA_VIEWER_QUERY_KEY} from "~/utilities/archiveEventContainerWithEvents";
import {YOUR_QUEUE_EVENT_CONTAINER_QUERY_KEY_ROOT} from "~/utilities/dayPlan/dayPlanEventContainerQuery";
import {refreshAmaronDispositionTrackerData} from "~/utilities/generateAmaronDispositionKanbanData";
import {
    DULUX_AGENT_HOME_QUERY_ROOT,
    DULUX_FOCUS_QUEUE_SUMMARY_QUERY_KEY,
} from "~/workspaceConfig/duluxAgentHome/queryKeys";

/**
 * Refresh every config-driven surface that can show the mutated row:
 * list/kanban/calendar pages, journey profile, profile Events tab, disposition tracker, agent home,
 * and the Dulux agent-home and day-plan focus queries.
 */
export async function invalidateConfigDrivenWorkspaceQueries(args: {
    queryClient: QueryClient;
    workspaceId: Uuid;
    queryKeyPrefix?: ReadonlyArray<string | null>;
}): Promise<void> {
    const prefixInvalidation =
        args.queryKeyPrefix === undefined
            ? Promise.resolve()
            : invalidateOttopilotDataViewerQueries(args.queryClient, args.queryKeyPrefix);

    await Promise.all([
        prefixInvalidation,
        args.queryClient.invalidateQueries({
            queryKey: ["ottopilotDataViewer", "workspace-config-copy", args.workspaceId],
            refetchType: "all",
        }),
        args.queryClient.invalidateQueries({
            queryKey: ["workspace-config-journey-profile", args.workspaceId],
            refetchType: "all",
        }),
        args.queryClient.invalidateQueries({
            queryKey: ["workspace-config-form-page"],
            refetchType: "all",
        }),
        invalidateOttopilotDataViewerQueries(args.queryClient, [JOURNEY_EVENTS_MODULE_DATA_VIEWER_QUERY_KEY]),
        refreshAmaronDispositionTrackerData(args.queryClient, args.workspaceId),
        args.queryClient.invalidateQueries({
            queryKey: ["home", "todayWorkStats", args.workspaceId],
            refetchType: "all",
        }),
        args.queryClient.invalidateQueries({
            queryKey: ["home", "engagementHealth", args.workspaceId],
            refetchType: "all",
        }),
        args.queryClient.invalidateQueries({
            queryKey: ["home", "performanceOverview", args.workspaceId],
            refetchType: "all",
        }),
        args.queryClient.invalidateQueries({
            queryKey: [YOUR_QUEUE_EVENT_CONTAINER_QUERY_KEY_ROOT, args.workspaceId],
            refetchType: "all",
        }),
        invalidateDuluxAgentViewQueries(args.queryClient, args.workspaceId),
    ]);
}

function queryKeyIncludes(queryKey: ReadonlyArray<unknown>, value: string): boolean {
    for (const segment of queryKey) {
        if (segment === value) {
            return true;
        }
    }
    return false;
}

function invalidateDuluxAgentViewQueries(queryClient: QueryClient, workspaceId: Uuid): Promise<void> {
    return queryClient.invalidateQueries({
        predicate: (query) => {
            const queryKey = query.queryKey;
            if (queryKey[0] === DULUX_AGENT_HOME_QUERY_ROOT && queryKey[2] === workspaceId) {
                return true;
            }
            if (
                queryKeyIncludes(queryKey, DULUX_FOCUS_QUEUE_SUMMARY_QUERY_KEY) === true &&
                queryKeyIncludes(queryKey, workspaceId) === true
            ) {
                return true;
            }
            return false;
        },
        refetchType: "all",
    });
}
